import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// Types for different history data structures
interface OcularHistoryData {
  ocularHistoryEyeInfectionComment?: string;
  ocularHistoryInjuryState?: string;
  ocularHistoryCataractsState?: string;
  ocularHistoryDiabeticRetinopathyComment?: string;
  ocularHistoryCataractsCurrentState?: string;
  ocularHistoryOtherEyeConditions?: string;
  ocularHistoryLazyCurrentState?: string;
  ocularHistoryMacularDiseaseState?: string;
  ocularHistoryInjuryComment?: string;
  ocularHistoryLensesComment?: string;
  ocularHistoryLensesState?: string;
  ocularHistoryLensesCurrentState?: string;
  ocularHistoryProminentState?: string;
  ocularHistoryCataractsComment?: string;
  ocularHistoryCornealDiseaseCurrentState?: string;
  ocularHistoryDiabeticEyeDiseaseState?: string;
  ocularHistoryDryEyeComment?: string;
  ocularHistoryCrossedComment?: string;
  ocularHistoryRetinalComment?: string;
  ocularHistoryEyeInfectionCurrentState?: string;
  ocularHistoryEyelidConditionComment?: string;
  ocularHistoryEyeInfectionState?: string;
  ocularHistoryProminentComment?: string;
  ocularHistoryRetinalState?: string;
  ocularHistoryDiabeticRetinopathyState?: string;
  ocularHistoryRedEyeCurrentState?: string;
  ocularHistoryGlaucomaCurrentState?: string;
  ocularHistoryDryEyeState?: string;
  ocularHistoryRedEyeState?: string;
  ocularHistoryMacularDiseaseComment?: string;
  ocularHistoryDroopingComment?: string;
  ocularHistoryGlassesState?: string;
  ocularHistoryDiabeticEyeDiseaseCurrentState?: string;
  ocularHistoryCrossedState?: string;
  ocularHistoryMacularDiseaseCurrentState?: string;
  ocularHistoryLazyState?: string;
  ocularHistoryProminentCurrentState?: string;
  ocularHistoryCornealDiseaseComment?: string;
  ocularHistoryGlaucomaComment?: string;
  ocularHistoryCrossedCurrentState?: string;
  ocularHistoryLazyComment?: string;
  ocularHistoryRedEyeComment?: string;
  ocularHistoryEyelidConditionCurrentState?: string;
  ocularHistoryDiabeticRetinopathyCurrentState?: string;
  ocularHistoryDryEyeCurrentState?: string;
  ocularHistoryDroopingState?: string;
  ocularHistoryCornealDiseaseState?: string;
  ocularHistoryDiabeticEyeDiseaseComment?: string;
  ocularHistoryGlaucomaState?: string;
  ocularHistoryGlassesComment?: string;
  ocularHistoryInjuryCurrentState?: string;
  ocularHistoryRetinalCurrentState?: string;
  ocularHistoryGlassesCurrentState?: string;
  ocularHistoryEyelidConditionState?: string;
  ocularHistoryDroopingCurrentState?: string;
}

interface MedicalHistoryData {
  medicalHistoryLastMedicalExam?: string;
  medicalHistoryVenerialCurrentState?: string;
  medicalHistoryCancerComment?: string;
  medicalHistoryPregnantComment?: string;
  medicalHistoryHeartDiseaseState?: string;
  medicalHistoryOtherMedicalConditions?: string;
  medicalHistoryArthritisComment?: string;
  medicalHistoryDiabetesCurrentState?: string;
  medicalHistoryThyroidState?: string;
  medicalHistoryThyroidComment?: string;
  medicalHistoryAllergiesComment?: string;
  medicalHistoryHeartDiseaseCurrentState?: string;
  medicalHistoryHypertensionComment?: string;
  medicalHistoryDiabetesComment?: string;
  medicalHistoryHeadachesComment?: string;
  medicalHistoryThyroidCurrentState?: string;
  medicalHistoryVenerialComment?: string;
  medicalHistoryNursingState?: string;
  medicalHistoryCancerState?: string;
  medicalHistoryAsthmaCurrentState?: string;
  medicalHistoryArthritisCurrentState?: string;
  medicalHistoryCancerCurrentState?: string;
  medicalHistoryHypertensionState?: string;
  medicalHistoryCholesterolComment?: string;
  medicalHistoryHeadachesCurrentState?: string;
  medicalHistoryAllergiesState?: string;
  medicalHistoryHIVState?: string;
  medicalHistoryAutoImmuneCurrentState?: string;
  medicalHistoryAllergiesCurrentState?: string;
  medicalHistoryHepatitisCurrentState?: string;
  medicalHistoryDiabetesState?: string;
  medicalHistoryHIVComment?: string;
  medicalHistoryNursingComment?: string;
  medicalHistoryAsthmaComment?: string;
  medicalHistoryAutoImmuneComment?: string;
  medicalHistoryHeadachesState?: string;
  medicalHistoryVenerialState?: string;
  medicalHistoryCholesterolState?: string;
  medicalHistoryHeartDiseaseComment?: string;
  medicalHistoryHIVCurrentState?: string;
  medicalHistoryAsthmaState?: string;
  medicalHistoryAutoImmuneState?: string;
  medicalHistoryPregnantState?: string;
  medicalHistoryHepatitisState?: string;
  medicalHistoryCholesterolCurrentState?: string;
  medicalHistoryHypertensionCurrentState?: string;
  medicalHistoryArthritisState?: string;
}

interface FamilyHistoryData {
  familyHistorySister?: string;
  familyHistoryGrandMother?: string;
  familyHistoryUnknownForChild?: string;
  familyHistoryBrother?: string;
  familyHistoryUnknownForMother?: string;
  familyHistoryUnknownForFather?: string;
  familyHistoryUnknownForBrother?: string;
  familyHistoryUnknownForGrandMother?: string;
  familyHistoryUncle?: string;
  familyHistoryUnknownForGrandFather?: string;
  familyHistoryLastUpdated?: string;
  familyHistoryUnknownForAunt?: string;
  familyHistoryChild?: string;
  familyHistoryComment?: string;
  familyHistoryUnknownForSister?: string;
  familyHistoryFather?: string;
  familyHistoryUnknownForUncle?: string;
  familyHistoryAunt?: string;
  familyHistoryMother?: string;
  familyHistoryGrandFather?: string;
}

interface SocialHistoryData {
  socialHistoryMaritalStatus?: string;
  socialHistoryNarcoticsUse?: string;
  socialHistoryLivingArrangementComments?: string;
  socialHistoryComputerUse?: string;
  socialHistorySmokingStatusStopDate?: string;
  socialHistorySmokingStatusComments?: string;
  socialHistoryNarcoticsUseComments?: string;
  socialHistoryAlcoholUse?: string;
  socialHistoryComputerUseComments?: string;
  socialHistoryOccupationComments?: string;
  socialHistoryDrivingDifficultiesState?: string;
  socialHistoryAlcoholUseComments?: string;
  socialHistoryCurrentlyDrivesState?: string;
  socialHistoryLivingArrangement?: string;
  socialHistorySmokingStatusNotDocumentedMedicalReasons?: string;
  socialHistoryDrivingDifficultiesComments?: string;
  socialHistoryCurrentlyDrivesComments?: string;
  socialHistoryMaritalStatusComments?: string;
  socialHistoryOccupation?: string;
  socialHistorySmokingStatus?: string;
  socialHistoryLastUpdated?: string;
}

interface MedicalSurgeryHistoryData {
  date?: string;
  clinicName?: string;
  surgeonName?: string;
  type?: string;
}

interface HistoriesData {
  ocularHistory?: OcularHistoryData;
  medicalHistory?: MedicalHistoryData;
  familyHistory?: FamilyHistoryData;
  socialHistory?: SocialHistoryData;
  ocularSurgeryHistory?: any; // Will be empty based on the data
  medicalSurgeryHistory?: MedicalSurgeryHistoryData[];
  medicalInjuryHistory?: any; // Will be empty based on the data
  socialHobbyHistory?: any; // Will be empty based on the data
}

// Interface for the new API response structure
interface HistoriesApiResponse {
  testKey: string;
  data: any[];
}

interface HistoriesSectionProps {
  jsonContent: HistoriesApiResponse[] | null;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: HistoriesApiResponse[]) => Promise<void>;
  onAccept?: () => void;
}

// Helper function to extract meaningful data for display (compact view)
const extractDisplayFields = (
  data: any,
  prefix: string
): Array<{ key: string; label: string; value: string }> => {
  if (!data) return [];

  const fields: Array<{ key: string; label: string; value: string }> = [];

  // Define priority fields that should be shown first if they have values
  const priorityFields: Record<string, string[]> = {
    ocularHistory: [
      'ocularHistoryOtherEyeConditions',
      'ocularHistoryInjuryComment',
      'ocularHistoryCataractsComment',
      'ocularHistoryDiabeticRetinopathyComment',
      'ocularHistoryDryEyeComment',
      'ocularHistoryGlaucomaComment',
      'ocularHistoryMacularDiseaseComment',
      'ocularHistoryRetinalComment',
      'ocularHistoryLensesComment',
      'ocularHistoryGlassesState',
      'ocularHistoryGlassesComment',
    ],
    medicalHistory: [
      'medicalHistoryOtherMedicalConditions',
      'medicalHistoryLastMedicalExam',
      'medicalHistoryDiabetesComment',
      'medicalHistoryHeartDiseaseComment',
      'medicalHistoryHypertensionComment',
      'medicalHistoryAllergiesComment',
      'medicalHistoryArthritisComment',
      'medicalHistoryAsthmaComment',
      'medicalHistoryThyroidComment',
      'medicalHistoryCancerComment',
    ],
    familyHistory: [
      'familyHistoryComment',
      'familyHistoryFather',
      'familyHistoryMother',
      'familyHistorySister',
      'familyHistoryBrother',
      'familyHistoryChild',
      'familyHistoryGrandFather',
      'familyHistoryGrandMother',
      'familyHistoryUncle',
      'familyHistoryAunt',
    ],
    socialHistory: [
      'socialHistoryOccupation',
      'socialHistoryMaritalStatus',
      'socialHistorySmokingStatus',
      'socialHistoryAlcoholUse',
      'socialHistoryComputerUse',
      'socialHistoryLivingArrangement',
      'socialHistoryOccupationComments',
      'socialHistorySmokingStatusComments',
      'socialHistoryAlcoholUseComments',
      'socialHistoryComputerUseComments',
    ],
  };

  const priorityList = priorityFields[prefix] || [];

  // First, add priority fields that have meaningful values
  priorityList.forEach((key) => {
    let value = data[key];
    if (
      value &&
      typeof value === 'string' &&
      value.trim() !== '' &&
      value !== 'NO' &&
      value !== 'UNKNOWN' &&
      value !== 'NOT_DOCUMENTED'
    ) {
      const label = key
        .replace(prefix, '')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      if (key === 'medicalHistoryLastMedicalExam') {
        const dateObj = new Date(+value);
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const yyyy = dateObj.getFullYear();
        value = `${mm}/${dd}/${yyyy}`;
      }

      fields.push({
        key,
        label,
        value: value as string,
      });
    }
  });

  // Then add other meaningful fields (limit to prevent overwhelming display)
  const maxDisplayFields = 8; // Limit display fields for compact view
  let addedCount = fields.length;

  Object.entries(data).forEach(([key, value]) => {
    if (addedCount >= maxDisplayFields) return; // Stop if we've reached the limit

    // Skip if already added as priority field
    if (priorityList.includes(key)) return;

    if (
      value &&
      typeof value === 'string' &&
      value.trim() !== '' &&
      value !== 'NO' &&
      value !== 'UNKNOWN' &&
      value !== 'NOT_DOCUMENTED' &&
      !key.includes('CurrentState') && // Skip current state fields in display
      !key.includes('State') && // Skip state fields in display
      !key.includes('LastUpdated') // Skip timestamp fields
    ) {
      const label = key
        .replace(prefix, '')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      fields.push({
        key,
        label,
        value: value as string,
      });
      addedCount++;
    }
  });

  return fields;
};

// Helper function to create editable fields for history data (all fields for edit dialog)
const createHistoryFields = (
  data: any,
  prefix: string
): Array<{
  key: string;
  label: string;
  value: string;
  placeholder?: string;
}> => {
  if (!data) return [];

  const fields: Array<{
    key: string;
    label: string;
    value: string;
    placeholder?: string;
  }> = [];

  Object.entries(data).forEach(([key, value]) => {
    // Convert camelCase to readable labels
    const label = key
      .replace(prefix, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    fields.push({
      key,
      label,
      value: (value as string) || '',
      placeholder: `Enter ${label.toLowerCase()}`,
    });
  });

  return fields;
};

const HistoriesSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: HistoriesSectionProps) => {
  const [currentData, setCurrentData] = useState<HistoriesData | null>(null);
  const [acceptedSections, setAcceptedSections] = useState<Set<string>>(
    new Set()
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper function to process API response into HistoriesData
  const processApiResponse = useCallback(
    (content: HistoriesApiResponse[] | null): HistoriesData | null => {
      if (!content) return null;

      // Process API response array with new structure
      const processedData: HistoriesData = {};

      content.forEach((item) => {
        if (item.data && item.data.length > 0) {
          const data = item.data[0]; // Get first item from data array

          switch (item.testKey) {
            case 'ocularhistory':
              processedData.ocularHistory = data;
              break;
            case 'medicalhistory':
              processedData.medicalHistory = data;
              break;
            case 'familyhistory':
              processedData.familyHistory = data;
              break;
            case 'socialhistory':
              processedData.socialHistory = data;
              break;
            case 'ocularhistory.surgery':
              processedData.ocularSurgeryHistory = data;
              break;
            case 'medicalhistory.surgery':
              processedData.medicalSurgeryHistory = item.data; // Keep as array for surgery history
              break;
            case 'medicalhistory.injury':
              processedData.medicalInjuryHistory = data;
              break;
            case 'socialhistory.hobby':
              processedData.socialHobbyHistory = data;
              break;
          }
        }
      });

      return processedData;
    },
    []
  );

  // Helper function to convert HistoriesData back to API response format
  const convertToApiResponse = useCallback(
    (data: HistoriesData): HistoriesApiResponse[] => {
      const result: HistoriesApiResponse[] = [];

      // Convert each history section back to API format
      if (data.ocularHistory) {
        result.push({
          testKey: 'ocularhistory',
          data: [data.ocularHistory],
        });
      }

      if (data.medicalHistory) {
        result.push({
          testKey: 'medicalhistory',
          data: [data.medicalHistory],
        });
      }

      if (data.familyHistory) {
        result.push({
          testKey: 'familyhistory',
          data: [data.familyHistory],
        });
      }

      if (data.socialHistory) {
        result.push({
          testKey: 'socialhistory',
          data: [data.socialHistory],
        });
      }

      // Handle surgery histories
      if (data.ocularSurgeryHistory) {
        result.push({
          testKey: 'ocularhistory.surgery',
          data: Array.isArray(data.ocularSurgeryHistory)
            ? data.ocularSurgeryHistory
            : [data.ocularSurgeryHistory],
        });
      }

      if (data.medicalSurgeryHistory) {
        result.push({
          testKey: 'medicalhistory.surgery',
          data: Array.isArray(data.medicalSurgeryHistory)
            ? data.medicalSurgeryHistory
            : [data.medicalSurgeryHistory],
        });
      }

      if (data.medicalInjuryHistory) {
        result.push({
          testKey: 'medicalhistory.injury',
          data: Array.isArray(data.medicalInjuryHistory)
            ? data.medicalInjuryHistory
            : [data.medicalInjuryHistory],
        });
      }

      if (data.socialHobbyHistory) {
        result.push({
          testKey: 'socialhistory.hobby',
          data: Array.isArray(data.socialHobbyHistory)
            ? data.socialHobbyHistory
            : [data.socialHobbyHistory],
        });
      }

      // Always include empty surgery/injury sections if they don't exist
      const existingTestKeys = new Set(result.map((item) => item.testKey));

      if (!existingTestKeys.has('ocularhistory.surgery')) {
        result.push({
          testKey: 'ocularhistory.surgery',
          data: [],
        });
      }

      if (!existingTestKeys.has('medicalhistory.injury')) {
        result.push({
          testKey: 'medicalhistory.injury',
          data: [],
        });
      }

      if (!existingTestKeys.has('socialhistory.hobby')) {
        result.push({
          testKey: 'socialhistory.hobby',
          data: [],
        });
      }

      return result;
    },
    []
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const processedData = processApiResponse(jsonContent);
    setCurrentData(processedData);
  }, [jsonContent, processApiResponse]);

  // Handle updates to individual history sections
  const handleHistoryUpdate = useCallback(
    async (
      historyType: keyof HistoriesData,
      updatedFields: Record<string, string>
    ) => {
      if (!currentData) return;

      const updatedData: HistoriesData = {
        ...currentData,
        [historyType]: {
          ...currentData[historyType],
          ...updatedFields,
        },
      };

      // Update local state optimistically
      setCurrentData(updatedData);

      try {
        // Convert to API response format before calling onUpdate
        const apiResponseData = convertToApiResponse(updatedData);
        console.log(
          '🔍 Individual update - Converted API response data:',
          apiResponseData
        );
        await onUpdate(apiResponseData);
      } catch (error) {
        // Revert local state on error
        const processedData = processApiResponse(jsonContent);
        setCurrentData(processedData);
        throw error;
      }
    },
    [currentData, jsonContent, onUpdate, convertToApiResponse]
  );

  // Handle edit button click for all sections
  const handleEditAllClick = useCallback(() => {
    if (!currentData) return;

    // Combine all history data into a single edit object
    const allEditValues: Record<string, any> = {};

    // Include ALL sections, not just ones with data, to allow editing empty sections
    const sections = [
      {
        key: 'ocularHistory',
        data: currentData?.ocularHistory || {},
        isArray: false,
      },
      {
        key: 'medicalHistory',
        data: currentData?.medicalHistory || {},
        isArray: false,
      },
      {
        key: 'familyHistory',
        data: currentData?.familyHistory || {},
        isArray: false,
      },
      {
        key: 'socialHistory',
        data: currentData?.socialHistory || {},
        isArray: false,
      },
      {
        key: 'medicalSurgeryHistory',
        data: currentData?.medicalSurgeryHistory || [],
        isArray: true,
      },
    ];

    sections.forEach((section) => {
      allEditValues[section.key] = section.data;
    });

    console.log('🔍 Opening edit dialog with values:', allEditValues);
    setEditValues(allEditValues);
    setIsEditDialogOpen(true);
  }, [currentData]);

  // Handle saving all edited values
  const handleSaveEdit = useCallback(async () => {
    if (!currentData) return;

    setIsUpdating(true);
    try {
      // Create updated data by properly merging each section
      const updatedData: HistoriesData = {
        ...currentData,
      };

      // Update each section individually to maintain proper structure
      Object.keys(editValues).forEach((sectionKey) => {
        if (editValues[sectionKey]) {
          updatedData[sectionKey as keyof HistoriesData] =
            editValues[sectionKey];
        }
      });

      // Debug logging
      console.log('🔍 Before save - currentData:', currentData);
      console.log('🔍 Edit values:', editValues);
      console.log('🔍 Updated data:', updatedData);

      // Update local state optimistically
      setCurrentData(updatedData);

      // Call the onUpdate function with the updated data
      const apiResponseData = convertToApiResponse(updatedData);
      console.log('🔍 Converted API response data:', apiResponseData);
      await onUpdate(apiResponseData);

      // Close the dialog
      setIsEditDialogOpen(false);
      setEditValues({});

      console.log('✅ Updated all history sections:', updatedData);
    } catch (error) {
      console.error('Failed to update history sections:', error);
      // Revert local state on error
      const processedData = processApiResponse(jsonContent);
      setCurrentData(processedData);
    } finally {
      setIsUpdating(false);
    }
  }, [
    editValues,
    currentData,
    onUpdate,
    jsonContent,
    processApiResponse,
    convertToApiResponse,
  ]);

  // Handle canceling edit
  const handleCancelEdit = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditValues({});
  }, []);

  // Handle input changes in edit form
  const handleInputChange = useCallback(
    (section: string, field: string, value: string) => {
      setEditValues((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    },
    []
  );

  // Handle Accept All functionality - moved after historySections definition

  // Check if a history section has meaningful data
  const hasHistoryData = (data: any, prefix: string): boolean => {
    if (!data) return false;
    return Object.values(data).some(
      (value) =>
        value &&
        typeof value === 'string' &&
        value.trim() !== '' &&
        value !== 'NO' &&
        value !== 'UNKNOWN' &&
        value !== 'NOT_DOCUMENTED'
    );
  };

  // Check if medical surgery history has data
  const hasMedicalSurgeryData = (data: any[]): boolean => {
    if (!data || !Array.isArray(data)) return false;
    return data.some(
      (item) =>
        item &&
        typeof item === 'object' &&
        Object.values(item).some(
          (value) => value && typeof value === 'string' && value.trim() !== ''
        )
    );
  };

  // Get all history sections (show sections even if they become empty after editing)
  const allHistorySections = [
    {
      key: 'ocularHistory',
      title: 'Ocular History',
      data: currentData?.ocularHistory,
      prefix: 'ocularHistory',
      hasData: hasHistoryData(currentData?.ocularHistory, 'ocularHistory'),
    },
    {
      key: 'medicalHistory',
      title: 'Medical History',
      data: currentData?.medicalHistory,
      prefix: 'medicalHistory',
      hasData: hasHistoryData(currentData?.medicalHistory, 'medicalHistory'),
    },
    {
      key: 'familyHistory',
      title: 'Family History',
      data: currentData?.familyHistory,
      prefix: 'familyHistory',
      hasData: hasHistoryData(currentData?.familyHistory, 'familyHistory'),
    },
    {
      key: 'socialHistory',
      title: 'Social History',
      data: currentData?.socialHistory,
      prefix: 'socialHistory',
      hasData: hasHistoryData(currentData?.socialHistory, 'socialHistory'),
    },
    {
      key: 'medicalSurgeryHistory',
      title: 'Medical Surgery History',
      data: currentData?.medicalSurgeryHistory,
      prefix: 'medicalSurgeryHistory',
      hasData: hasMedicalSurgeryData(currentData?.medicalSurgeryHistory),
      isArray: true,
    },
  ];

  // Get history sections that have data
  const historySections = allHistorySections.filter(
    (section) => section.hasData
  );

  // Handle Accept All functionality
  const handleAcceptAll = useCallback(() => {
    if (!currentData) return;

    // Mark all sections as accepted (use all sections, not just ones with data)
    const allSections = new Set(
      allHistorySections.map((section) => section.key)
    );
    setAcceptedSections(allSections);

    // Call global accept only once (this should handle all the data)
    if (onAccept) {
      onAccept();
    }

    console.log('✅ Accepted all history sections with single API call');
  }, [currentData, allHistorySections, onAccept]);

  // Check if any history sections have data
  const hasAnyHistoryData = historySections.length > 0;

  if (!hasAnyHistoryData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                  Histories
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}>
                  <Loader2 className="w-4 h-4" />
                </motion.div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}>
                  Fetching section content...
                </motion.span>
              </motion.div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400 text-md italic">
                No histories data available yet
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        {/* Main Histories Card with Integrated Subsections */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            {/* Main Header */}
            <div className="p-4 border-b border-white/20 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
                    Histories
                  </h3>
                  {isLoading && (
                    <div className="flex items-center gap-1 ml-2">
                      <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>
                {hasAnyHistoryData && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAllClick();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <Edit3 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptAll();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  </div>
                )}
              </div>

              {error && (
                <div className="text-slate-500 dark:text-slate-400 text-md italic mt-2">
                  {error === 'Data not found' ? (
                    'No histories data available yet'
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Error:</span> {error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Individual History Subsections */}
            <div className="p-4 space-y-4">
              {historySections.map((section, sectionIndex) => {
                const isAccepted = acceptedSections.has(section.key);

                // Handle medical surgery history differently since it's an array
                if (
                  section.key === 'medicalSurgeryHistory' &&
                  section.isArray
                ) {
                  const surgeryData =
                    section.data as MedicalSurgeryHistoryData[];
                  const surgeryFields = surgeryData.map((surgery, index) => ({
                    key: `surgery_${index}`,
                    label: `Surgery ${index + 1}`,
                    value:
                      surgery.type ||
                      surgery.clinicName ||
                      surgery.surgeonName ||
                      surgery.date ||
                      '',
                    placeholder: 'Enter surgery details',
                  }));

                  return (
                    <div
                      key={section.key}
                      className={`${
                        sectionIndex > 0
                          ? 'border-t border-gray-400/40 dark:border-white/10 pt-4'
                          : ''
                      }`}>
                      {/* Subsection Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="text-slate-700 dark:text-slate-300 font-semibold text-lg">
                            {section.title}
                          </h4>
                          {isAccepted && (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                      </div>

                      {/* Surgery History Content */}
                      <div className="space-y-1">
                        {surgeryFields.map((field, fieldIndex) => (
                          <div
                            key={field.key}
                            className="p-1 bg-slate-100/70 dark:bg-slate-800/50 rounded-xl border border-slate-200/30 dark:border-slate-700/30 hover:bg-slate-100/70 dark:hover:bg-slate-700/50 transition-colors duration-200">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                              <div className="text-md font-semibold text-slate-700 dark:text-slate-300 min-w-0 sm:min-w-[140px]">
                                {field.label}
                              </div>
                              <div className="text-md text-slate-600 dark:text-slate-400 break-words flex-1">
                                {field.value === 'YES'
                                  ? 'Yes'
                                  : field.value === 'NO'
                                  ? 'No'
                                  : field.value || 'Not specified'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Handle regular history sections
                const displayFields = extractDisplayFields(
                  section.data,
                  section.prefix
                );

                return (
                  <div
                    key={section.key}
                    className={`${
                      sectionIndex > 0
                        ? 'border-t border-gray-400/40 dark:border-white/10 pt-4'
                        : ''
                    }`}>
                    {/* Subsection Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-slate-700 dark:text-slate-300 font-semibold text-lg">
                          {section.title}
                        </h4>
                        {isAccepted && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                      </div>
                    </div>

                    {/* History Content */}
                    <div className="space-y-1">
                      {displayFields.map((field) => (
                        <div
                          key={field.key}
                          className="pl-2 p-1 bg-slate-100/70 dark:bg-slate-800/50 rounded-xl border border-slate-200/30 dark:border-slate-700/30 hover:bg-slate-100/70 dark:hover:bg-slate-700/50 transition-colors duration-200">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <div className="text-md font-semibold text-slate-700 dark:text-slate-300 min-w-0 sm:min-w-[140px]">
                              {field.label}
                            </div>
                            <div className="text-md text-slate-600 dark:text-slate-400 break-words flex-1">
                              {field.value === 'YES'
                                ? 'Yes'
                                : field.value === 'NO'
                                ? 'No'
                                : field.value || 'Not specified'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 text-xl">
              Edit Histories
            </DialogTitle>
          </DialogHeader>

          <div className="pl-1 pr-1 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Render each history section for editing */}
            {allHistorySections.map((section) => (
              <div key={section.key} className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-400/40 dark:border-slate-700 pb-2">
                  {section.title}
                </h4>

                {section.isArray ? (
                  // Handle medical surgery history array
                  <div className="space-y-4">
                    {(
                      (editValues[
                        section.key
                      ] as MedicalSurgeryHistoryData[]) || []
                    ).map((surgery, index) => (
                      <div
                        key={index}
                        className="p-4 bg-slate-200/50 dark:bg-slate-800/30 rounded-lg border border-slate-400/40 dark:border-slate-700/30">
                        <h5 className="text-md font-semibold text-slate-600 dark:text-slate-400 mb-3">
                          Surgery {index + 1}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(surgery || {}).map(([key, value]) => (
                            <div key={key} className="space-y-2">
                              <Label className="text-sm text-slate-600 dark:text-slate-400">
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </Label>
                              <Input
                                value={value || ''}
                                onChange={(e) => {
                                  const updatedSurgeries = [
                                    ...((editValues[
                                      section.key
                                    ] as MedicalSurgeryHistoryData[]) || []),
                                  ];
                                  updatedSurgeries[index] = {
                                    ...updatedSurgeries[index],
                                    [key]: e.target.value,
                                  };
                                  setEditValues((prev) => ({
                                    ...prev,
                                    [section.key]: updatedSurgeries,
                                  }));
                                }}
                                className="h-10 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Handle regular history objects
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(editValues[section.key] || {}).map(
                      ([key, value]) => {
                        const label = key
                          .replace(section.prefix, '')
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, (str) => str.toUpperCase())
                          .trim();

                        return (
                          <div key={key} className="space-y-2">
                            <Label className="text-md text-slate-600 dark:text-slate-400">
                              {label}
                            </Label>
                            <Input
                              value={String(value || '')}
                              onChange={(e) =>
                                handleInputChange(
                                  section.key,
                                  key,
                                  e.target.value
                                )
                              }
                              className="h-10 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HistoriesSection;
